import { SCENE_W, type TeamId, type WeaponId } from '@rekrut/shared';
import { ARENA_BOTTOM, ARENA_TOP } from '@rekrut/shared';
import type { Engine } from './engine.js';
import { other, type Unit } from './state.js';

/**
 * Оружие за подарки. Чем дороже подарок, тем тяжелее удар (ТЗ §7).
 *
 * Общее правило для всех ступеней: трон неуязвим, пока у его команды есть
 * хоть один живой боец. Самая дорогая ступень бьёт по площади насмерть,
 * но только по рядовым и не более трети живых — иначе один донат стирает
 * поле, и смотреть дальше нечего.
 */

export interface WeaponMeta {
  id: WeaponId;
  name: string;
  icon: string;
  color: number;
}

export const WEAPON_META: Record<WeaponId, WeaponMeta> = {
  volley: { id: 'volley', name: 'ВИНЧЕСТЕР', icon: '🌹', color: 0xffffff },
  laser: { id: 'laser', name: 'ЛАЗЕР', icon: '🔺', color: 0xff5cf0 },
  rocket: { id: 'rocket', name: 'РАКЕТЫ', icon: '🚀', color: 0xffa63d },
  barrage: { id: 'barrage', name: 'БОЛЬШОЙ ЗАЛП', icon: '💥', color: 0xff7a2f },
  orbital: { id: 'orbital', name: 'ОРБИТАЛЬНЫЙ УДАР', icon: '☄️', color: 0x9b59ff },
  purge: { id: 'purge', name: 'КАРА', icon: '☠️', color: 0xff2f5e },
};

/** Скорость отброса при залпе, px/с. Гаснет за пару десятых секунды. */
const KNOCKBACK_SPEED = 900;

const scratch: Unit[] = [];

/** Точка приложения удара: центр массы врагов, иначе — вражеский трон. */
function enemyFocus(engine: Engine, team: TeamId): { x: number; y: number } {
  const enemy = other(team);
  let sx = 0;
  let sy = 0;
  let n = 0;
  for (const u of engine.state.unitList) {
    if (u.team !== enemy) continue;
    sx += u.x;
    sy += u.y;
    n++;
  }
  if (n === 0) {
    const th = engine.state.thrones[enemy];
    return { x: th.x, y: th.y };
  }
  return { x: sx / n, y: sy / n };
}

export function fireWeapon(engine: Engine, id: WeaponId, unit: Unit, nickname: string): void {
  switch (id) {
    case 'laser':
      return laser(engine, unit, nickname);
    case 'rocket':
      return salvo(engine, unit, nickname, 'rocket');
    case 'barrage':
      return salvo(engine, unit, nickname, 'barrage');
    case 'orbital':
      return orbital(engine, unit, nickname);
    case 'purge':
      return purge(engine, unit, nickname);
    case 'volley':
      return volley(engine, unit, nickname);
  }
}

/**
 * Роза за 1💎: очередь из ста белых пуль, разбитая на волны.
 *
 * Урон каждой — как у рядового выстрела, вся ценность в количестве. Сто пуль
 * разом — это одна вспышка и мгновенно опустевший пул снарядов; четыре волны
 * по двадцать пять читаются как очередь и держат экран почти секунду.
 */
function volley(engine: Engine, u: Unit, nickname: string): void {
  const waves = Math.max(1, engine.cfg.weapons.volley.waves);
  engine.queueVolley(u.userId, u.team, u.x, u.y, waves);
  cast(engine, u, nickname, 'volley');
}

/** Одна волна очереди: четверть пуль веером из текущей точки стрелка. */
export function fireVolleyWave(
  engine: Engine,
  ownerId: string,
  team: TeamId,
  x: number,
  y: number,
): void {
  const s = engine.state;
  const cfg = engine.cfg.weapons.volley;
  const perWave = Math.max(1, Math.round(cfg.count / Math.max(1, cfg.waves)));
  const focus = enemyFocus(engine, team);
  const base = Math.atan2(focus.y - y, focus.x - x);

  let fired = 0;
  for (let i = 0; i < perWave; i++) {
    const p = engine.acquireProjectile();
    if (!p) break;
    // Разлёт неравномерный: к краям веера пуль меньше, как при россыпи.
    const a = base + engine.rng.jitter(cfg.spread) * (0.4 + engine.rng.next() * 0.6);
    p.active = true;
    p.x = x + Math.cos(a) * 18;
    p.y = y + Math.sin(a) * 18;
    p.vx = Math.cos(a) * cfg.speed * (0.85 + engine.rng.next() * 0.3);
    p.vy = Math.sin(a) * cfg.speed * (0.85 + engine.rng.next() * 0.3);
    p.team = team;
    p.ownerId = ownerId;
    p.damage = cfg.damage;
    p.blastRadius = 0;
    // Тот же принцип: сто пуль не должны снести трон в одиночку.
    p.throneDamage = cfg.throneDamage / cfg.count;
    p.tint = 0xffffff;
    p.spiral = 0;
    p.tx = 0;
    p.ty = 0;
    p.diesAt = s.now + 2200;
    fired++;
  }
  s.stats.shotsFired += fired;
  s.fx.push({ kind: 'volleywave', x, y });
}

/** Луч от аватарки через всю карту в сторону чужого трона. */
function laser(engine: Engine, u: Unit, nickname: string): void {
  const s = engine.state;
  const cfg = engine.cfg.weapons.laser;
  const enemy = other(u.team);
  const th = s.thrones[enemy];

  const dx = th.x - u.x;
  const dy = th.y - u.y;
  const len = Math.hypot(dx, dy) || 1;
  const nx = dx / len;
  const ny = dy / len;
  // Луч идёт до края арены, а не до трона.
  const far = 2600;
  const x2 = u.x + nx * far;
  const y2 = u.y + ny * far;

  // Луч красится в цвет команды: у красных красный, у синих синий. Общий
  // розовый для обеих сторон не давал понять, кто по кому бьёт.
  s.fx.push({
    kind: 'laser',
    x1: u.x,
    y1: u.y,
    x2,
    y2,
    color: s.teams[u.team].color,
    width: cfg.width,
  });

  const half = cfg.width / 2;
  for (const t of s.unitList) {
    if (t.team !== enemy) continue;
    if (distToSegment(t.x, t.y, u.x, u.y, x2, y2) > half + t.radius) continue;
    engine.damageUnit(t, cfg.damage, u, false);
  }
  if (distToSegment(th.x, th.y, u.x, u.y, x2, y2) <= half + engine.cfg.arena.throneRadius) {
    engine.damageThrone(enemy, cfg.throneDamage, u.userId);
  }
  cast(engine, u, nickname, 'laser');
}

/**
 * Ракетный залп.
 *
 * Ракеты уходят веером и идут по спирали каждая в СВОЮ точку — туда, где на
 * момент пуска стоял конкретный противник. Если противников нет, все идут
 * в трон. Самонаведения нет намеренно: ракета, приклеенная к цели, летит
 * скучной прямой, а расходящийся веер по спиралям читается как залп.
 */
function salvo(engine: Engine, u: Unit, nickname: string, kind: 'rocket' | 'barrage'): void {
  const s = engine.state;
  const cfg = engine.cfg.weapons[kind];
  const enemy = other(u.team);
  const focus = enemyFocus(engine, u.team);
  const th = s.thrones[enemy];

  // Точки прицеливания: ближайшие к центру масс враги, по одному на ракету.
  const marks = s.unitList
    .filter((t) => t.team === enemy)
    .sort((a, b) => dist2(a, focus) - dist2(b, focus))
    .slice(0, cfg.count)
    .map((t) => ({ x: t.x, y: t.y }));
  if (marks.length === 0) marks.push({ x: th.x, y: th.y });

  const baseAngle = Math.atan2(focus.y - u.y, focus.x - u.x);

  for (let i = 0; i < cfg.count; i++) {
    const p = engine.acquireProjectile();
    if (!p) break;
    const mark = marks[i % marks.length]!;
    // Старт веером в стороны, дальше спираль стягивает ракету к точке.
    const spread = ((i - (cfg.count - 1) / 2) / Math.max(1, cfg.count - 1)) * 1.6;
    const a = baseAngle + spread;

    p.active = true;
    p.x = u.x;
    p.y = u.y;
    p.vx = Math.cos(a) * cfg.speed;
    p.vy = Math.sin(a) * cfg.speed;
    p.team = u.team;
    p.ownerId = u.userId;
    p.damage = cfg.damage;
    p.blastRadius = cfg.radius;
    // Суммарный урон по трону делится между ракетами залпа.
    p.throneDamage = cfg.throneDamage / cfg.count;
    p.tx = mark.x + engine.rng.jitter(28);
    p.ty = mark.y + engine.rng.jitter(28);
    // Соседние ракеты закручиваются в разные стороны.
    p.spiral = 0.95;
    p.spinPhase = i * 1.9;
    p.spinRate = (i % 2 === 0 ? 1 : -1) * engine.rng.range(3.4, 4.6);
    p.diesAt = s.now + 5000;
  }

  cast(engine, u, nickname, kind);
}

/**
 * Аватарка автора всплывает, раздувается и мигает в момент залпа — чтобы
 * зритель видел, КТО ударил. Плашки с названием оружия для этого не нужны:
 * они перекрывают поле и читаются хуже, чем сам автор на экране.
 */
function cast(engine: Engine, u: Unit, nickname: string, kind: WeaponId): void {
  const meta = WEAPON_META[kind];
  const th = engine.state.thrones[other(u.team)];

  // Отдача — строго против выстрела. Красные бьют вниз и их отбрасывает
  // вверх, синие бьют вверх и летят вниз. Один и тот же подскок для обеих
  // сторон выглядит как ошибка: половина экрана дёргается «в сторону удара».
  const dx = th.x - u.x;
  const dy = th.y - u.y;
  const len = Math.hypot(dx, dy) || 1;

  // Отметка ставится на РЕАЛЬНЫЙ объект — на бойца на поле либо на его
  // запись в очереди. Рисовать отдельную копию аватарки нельзя: оригинал
  // продолжает идти вперёд, и на экране получается раздвоение.
  const live = engine.state.units.get(u.userId);
  if (live) {
    live.castAt = engine.state.now;
    live.castDirX = -dx / len;
    live.castDirY = -dy / len;
    // Настоящий импульс: шар физически отбрасывает против выстрела.
    live.kbX = (-dx / len) * KNOCKBACK_SPEED;
    live.kbY = (-dy / len) * KNOCKBACK_SPEED;
  } else {
    for (const tid of ['A', 'B'] as const) {
      const e = engine.state.teams[tid].queue.find((q) => q.userId === u.userId);
      if (!e) continue;
      e.castAt = engine.state.now;
      e.castDirX = -dx / len;
      e.castDirY = -dy / len;
      break;
    }
  }

  // Вспышка и ударные волны — это эффекты в точке залпа, дублей аватарки нет.
  engine.state.fx.push({
    kind: 'cast',
    userId: u.userId,
    avatarUrl: u.avatarUrl,
    x: u.x,
    y: u.y,
    color: meta.color,
    ms: 1700,
    sfx: kind === 'laser' ? 'laser' : kind === 'volley' ? 'none' : 'rocket',
  });

  engine.tickerPush(`@${nickname} — ${meta.name.toLowerCase()}`, meta.color, 2000);
}

/** Мгновенный удар по площади без полёта снаряда. */
function orbital(engine: Engine, u: Unit, nickname: string): void {
  const s = engine.state;
  const cfg = engine.cfg.weapons.orbital;
  const enemy = other(u.team);
  const focus = enemyFocus(engine, u.team);

  s.fx.push({ kind: 'explosion', x: focus.x, y: focus.y, radius: cfg.radius });
  s.fx.push({ kind: 'shock', x: focus.x, y: focus.y, radius: cfg.radius, color: WEAPON_META.orbital.color });
  s.fx.push({ kind: 'screenshake', power: 12 });

  for (const t of engine.unitsInRadius(focus.x, focus.y, cfg.radius, enemy, scratch)) {
    engine.damageUnit(t, cfg.damage, u, false);
  }
  const th = s.thrones[enemy];
  if (Math.hypot(th.x - focus.x, th.y - focus.y) <= cfg.radius + engine.cfg.arena.throneRadius) {
    engine.damageThrone(enemy, cfg.throneDamage, u.userId);
  }
  cast(engine, u, nickname, 'orbital');
}

/**
 * Верхняя ступень. Мгновенно выкашивает рядовых в области, но не больше
 * заданной доли живых бойцов противника: даже самый крупный донат не должен
 * стирать поле целиком.
 */
function purge(engine: Engine, u: Unit, nickname: string): void {
  const s = engine.state;
  const cfg = engine.cfg.weapons.purge;
  const enemy = other(u.team);
  const focus = enemyFocus(engine, u.team);

  const aliveEnemies = engine.countAlive(enemy);
  const cap = Math.max(1, Math.floor(aliveEnemies * cfg.maxFraction));

  const victims = engine
    .unitsInRadius(focus.x, focus.y, cfg.radius, enemy, scratch)
    .filter((t) => t.level <= cfg.maxLevel)
    .sort((a, b) => dist2(a, focus) - dist2(b, focus))
    .slice(0, cap);

  s.fx.push({ kind: 'shock', x: focus.x, y: focus.y, radius: cfg.radius, color: WEAPON_META.purge.color });
  s.fx.push({ kind: 'screenshake', power: 22 });

  for (const t of victims) {
    t.hp = 0;
    engine.kill(t, u);
  }
  const th = s.thrones[enemy];
  if (Math.hypot(th.x - focus.x, th.y - focus.y) <= cfg.radius + engine.cfg.arena.throneRadius) {
    engine.damageThrone(enemy, cfg.throneDamage, u.userId);
  }
  cast(engine, u, nickname, 'purge');
}

function dist2(u: Unit, p: { x: number; y: number }): number {
  return (u.x - p.x) ** 2 + (u.y - p.y) ** 2;
}

/** Расстояние от точки до отрезка — нужно для попаданий лазера. */
function distToSegment(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(px - x1, py - y1);
  let t = ((px - x1) * dx + (py - y1) * dy) / len2;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

export { ARENA_BOTTOM, ARENA_TOP, SCENE_W };
